import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Eye, EyeOff } from "lucide-react";
import { useData } from "../../context/DataContext";
import { formatCurrency } from "../../lib/format";

const blank = {
    name: "",
    category: "Sedan",
    seats: 4,
    ratePerDay: 1900,
    kmLimit: 100,
    image: "",
    description: "",
    active: true,
};

const ManageCabs = () => {
    const { cabs, addCab, updateCab, deleteCab } = useData();
    const [editing, setEditing] = useState(null); // null | "new" | id
    const [form, setForm] = useState(blank);

    const open = (cab = null) => {
        if (cab) {
            setEditing(cab.id);
            setForm(cab);
        } else {
            setEditing("new");
            setForm(blank);
        }
    };

    const close = () => {
        setEditing(null);
        setForm(blank);
    };

    const save = (e) => {
        e.preventDefault();
        if (!form.name) return toast.error("Name is required.");
        if (editing === "new") {
            addCab(form);
            toast.success("Cab added.");
        } else {
            updateCab(editing, form);
            toast.success("Cab updated.");
        }
        close();
    };

    const remove = (id) => {
        if (window.confirm("Delete this cab?")) {
            deleteCab(id);
            toast.success("Cab deleted.");
        }
    };

    const toggleActive = (cab) => updateCab(cab.id, { active: !cab.active });

    return (
        <div data-testid="admin-cabs-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl text-ink">Manage Cabs</h1>
                    <p className="text-ink-light text-sm mt-1">{cabs.length} vehicles in fleet</p>
                </div>
                <button onClick={() => open()} className="px-4 py-2.5 rounded-xl bg-saffron-500 hover:bg-saffron-600 text-white font-semibold flex items-center gap-2 transition shadow-md" data-testid="admin-cab-add">
                    <Plus className="w-4 h-4" /> Add cab
                </button>
            </div>

            <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left text-xs uppercase tracking-wider text-ink-light">
                                <th className="px-4 py-3 font-semibold">Cab</th>
                                <th className="px-4 py-3 font-semibold">Category</th>
                                <th className="px-4 py-3 font-semibold">Seats</th>
                                <th className="px-4 py-3 font-semibold">Rate / day</th>
                                <th className="px-4 py-3 font-semibold">KM/day</th>
                                <th className="px-4 py-3 font-semibold">Status</th>
                                <th className="px-4 py-3 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {cabs.map((c) => (
                                <tr key={c.id} className="hover:bg-slate-50" data-testid={`admin-cab-${c.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <img src={c.image} alt={c.name} className="w-12 h-9 rounded-md object-cover bg-slate-100" />
                                            <div>
                                                <div className="font-semibold text-ink">{c.name}</div>
                                                <div className="text-xs text-ink-light line-clamp-1 max-w-xs">{c.description}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-ink-light">{c.category}</td>
                                    <td className="px-4 py-3 text-ink-light">{c.seats}</td>
                                    <td className="px-4 py-3 font-semibold text-saffron-600">{formatCurrency(c.ratePerDay)}</td>
                                    <td className="px-4 py-3 text-ink-light">{c.kmLimit}</td>
                                    <td className="px-4 py-3">
                                        <button onClick={() => toggleActive(c)} className={`text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${c.active ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-ink-light"}`} data-testid={`admin-cab-toggle-${c.id}`}>
                                            {c.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                            {c.active ? "Active" : "Hidden"}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => open(c)} className="p-2 rounded-md hover:bg-slate-100 text-ink-light" data-testid={`admin-cab-edit-${c.id}`}><Edit3 className="w-4 h-4" /></button>
                                        <button onClick={() => remove(c.id)} className="p-2 rounded-md hover:bg-red-50 text-red-600" data-testid={`admin-cab-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" onClick={close}>
                    <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-display text-2xl text-ink">{editing === "new" ? "Add cab" : "Edit cab"}</h2>
                            <button onClick={close} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                                <Field label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
                                <Field label="Seats" type="number" value={form.seats} onChange={(v) => setForm({ ...form, seats: Number(v) })} />
                                <Field label="Rate per day (₹)" type="number" value={form.ratePerDay} onChange={(v) => setForm({ ...form, ratePerDay: Number(v) })} />
                                <Field label="KM limit / day" type="number" value={form.kmLimit} onChange={(v) => setForm({ ...form, kmLimit: Number(v) })} />
                                <Field label="Image URL" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
                                <div className="md:col-span-2">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Description</label>
                                    <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white" />
                                </div>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-saffron-500 w-4 h-4" />
                                    Show on public site
                                </label>
                            </div>
                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                                <button type="button" onClick={close} className="px-4 py-2 rounded-lg border border-slate-200 text-ink font-semibold text-sm">Cancel</button>
                                <button type="submit" className="px-5 py-2 rounded-lg bg-saffron-500 hover:bg-saffron-600 text-white font-semibold text-sm" data-testid="admin-cab-save">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const Field = ({ label, type = "text", value, onChange, required }) => (
    <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">{label}{required && " *"}</label>
        <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white" />
    </div>
);

export default ManageCabs;
